/**
 * POSReceiptPrint — print-friendly чек 80mm для POS-друкарки.
 *
 * Окремий route /pos/receipt/:id/print, мінімальний layout.
 * @media print ховає actions (Друкувати / Назад).
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { retailSales } from '@/api/retailSales';
import { orgs } from '@/api/catalogs';
import './pos.css';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function POSReceiptPrint() {
  const { id } = useParams();
  const navigate = useNavigate();

  const saleQ = useQuery({
    queryKey: ['pos-receipt-print', id],
    queryFn: () => retailSales.get(id!),
    enabled: !!id,
  });

  const orgQ = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  if (saleQ.isLoading || !saleQ.data) {
    return <div style={{ padding: '2rem' }}>Завантаження...</div>;
  }

  const sale = saleQ.data;
  const org = orgQ.data?.items.find((o) => o.id === sale.organization_id);

  return (
    <>
      <div className="pos-receipt-print__actions">
        <button onClick={() => window.print()} className="primary">
          🖨 Друк / PDF
        </button>
        <button onClick={() => navigate(-1)} style={{ marginLeft: '0.5rem' }}>
          ← Назад
        </button>
      </div>

      <div className="pos-receipt-print">
        <div className="pos-receipt-print__header">
          {org?.full_name || org?.name || 'Організація'}
          <br />
          {org?.edrpou && <>ЄДРПОУ {org.edrpou}<br /></>}
          <br />
          ФIСКАЛЬНИЙ ЧЕК<br />
          № {sale.document_number}
        </div>
        <div className="pos-receipt-print__sep" />
        <div>
          {new Date(sale.document_date).toLocaleString('uk-UA')}
          <br />
          Касир: {sale.cashier_user_id.slice(0, 8)}
        </div>
        <div className="pos-receipt-print__sep" />

        {sale.items.map((it) => (
          <div key={it.id}>
            <div>{it.line_number}. (nomenclature {it.nomenclature_id.slice(0, 8)})</div>
            <div className="pos-receipt-print__row">
              <span>
                {fmt(it.quantity)} × {fmt(it.price_per_unit)}
              </span>
              <span>{fmt(it.total ?? Number(it.quantity) * Number(it.price_per_unit))}</span>
            </div>
          </div>
        ))}

        <div className="pos-receipt-print__sep" />

        <div className="pos-receipt-print__row">
          <span>Підсумок:</span>
          <span>{fmt(sale.subtotal)}</span>
        </div>
        {Number(sale.discount_amount) > 0 && (
          <div className="pos-receipt-print__row">
            <span>Знижка:</span>
            <span>−{fmt(sale.discount_amount)}</span>
          </div>
        )}
        {Number(sale.bonus_writeoff_amount) > 0 && (
          <div className="pos-receipt-print__row">
            <span>Списано бонусів:</span>
            <span>−{fmt(sale.bonus_writeoff_amount)}</span>
          </div>
        )}
        <div className="pos-receipt-print__row pos-receipt-print__total">
          <span>ДО СПЛАТИ:</span>
          <span>{fmt(sale.total_amount)}</span>
        </div>
        <div className="pos-receipt-print__sep" />
        <div className="pos-receipt-print__row">
          <span>Сплачено ({sale.payment_method}):</span>
          <span>
            {fmt(Number(sale.cash_amount) + Number(sale.card_amount))}
          </span>
        </div>
        {Number(sale.change_amount) > 0 && (
          <div className="pos-receipt-print__row">
            <span>Решта:</span>
            <span>{fmt(sale.change_amount)}</span>
          </div>
        )}
        {Number(sale.bonus_accrual_amount) > 0 && (
          <div className="pos-receipt-print__row">
            <span>Нараховано бонусів:</span>
            <span>+{fmt(sale.bonus_accrual_amount)}</span>
          </div>
        )}

        <div className="pos-receipt-print__sep" />

        {sale.fiscal_number ? (
          <div style={{ textAlign: 'center' }}>
            ФIСК. №: {sale.fiscal_number}
            <br />
            {sale.fiscal_status === 'demo' && '(ДЕМО-РЕЖИМ)'}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#666' }}>
            НЕФIСКАЛЬНИЙ ЧЕК
          </div>
        )}

        <div className="pos-receipt-print__sep" />
        <div style={{ textAlign: 'center', fontSize: '0.85em' }}>
          Дякуємо за покупку!
        </div>
      </div>
    </>
  );
}
